import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Flex, Heading } from '@radix-ui/themes';
import { Accordion, Callout, PillToggle } from '@/components/common';
import { WizardScreen } from '@/components/layout';
import { ContinueButton, OnboardingWizardHeader } from '@/components/onboarding';
import { V90_GENDER_OPTIONS, V90_ORIENTATION_OPTIONS, V90_PRONOUN_OPTIONS } from '@/constants';
import { useOnboardingStepSchemas, validateOnboardingStep } from '@/lib/validators';
import { useOnboardingStore } from '@/stores/onboardingStore';
import type { OnboardingStepProps } from './types';
import { useSaveAndNext } from './useSaveAndNext';

const MAX_IDENTITY_SELECTIONS = 3;

export function IdentityStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation('onboarding');
  const selectedGenders = useOnboardingStore(state =>
    Array.isArray(state.selectedGenders) ? state.selectedGenders : []
  );
  const selectedOrientations = useOnboardingStore(state =>
    Array.isArray(state.selectedOrientations) ? state.selectedOrientations : []
  );
  const pronouns = useOnboardingStore(state => state.pronouns);
  const setSelectedGenders = useOnboardingStore(state => state.setSelectedGenders);
  const setSelectedOrientations = useOnboardingStore(state => state.setSelectedOrientations);
  const setPronouns = useOnboardingStore(state => state.setPronouns);
  const saveAndNext = useSaveAndNext(flow);
  const stepSchemas = useOnboardingStepSchemas();
  const isValid = useMemo(
    () =>
      validateOnboardingStep(
        flow.step,
        { selectedGenders, selectedOrientations, pronouns },
        stepSchemas
      ).isValid,
    [selectedGenders, selectedOrientations, pronouns, flow.step, stepSchemas]
  );

  const genderDisabled = V90_GENDER_OPTIONS.filter(
    o => !selectedGenders.includes(o) && selectedGenders.length >= MAX_IDENTITY_SELECTIONS
  );
  const orientationDisabled = V90_ORIENTATION_OPTIONS.filter(
    o => !selectedOrientations.includes(o) && selectedOrientations.length >= MAX_IDENTITY_SELECTIONS
  );

  return (
    <WizardScreen header={<OnboardingWizardHeader flow={flow} />}>
      <Flex direction="column" gap="4" py="2" pb="5">
        <Heading size="5" weight="light">
          {t('steps.identity.title')}
        </Heading>
        <Callout>{t('steps.identity.callout')}</Callout>
        <Accordion
          type="multiple"
          gap="4"
          // TODO: replace spot-cat-accordion--brand with a Radix Themes variant on Accordion (e.g. color="red" / token props) — drop index.css hook
          className="spot-cat-accordion--brand"
        >
          <Accordion.Item
            value="gender"
            title={t('steps.identity.gender')}
            subtitle={t('steps.identity.genderTag')}
          >
            <PillToggle
              type="multiple"
              options={V90_GENDER_OPTIONS}
              value={selectedGenders}
              onValueChange={setSelectedGenders}
              disabledOptions={genderDisabled}
              dataTestId="gender"
            />
          </Accordion.Item>
          <Accordion.Item
            value="orientation"
            title={t('steps.identity.orientation')}
            subtitle={t('steps.identity.orientationTag')}
          >
            <PillToggle
              type="multiple"
              options={V90_ORIENTATION_OPTIONS}
              value={selectedOrientations}
              onValueChange={setSelectedOrientations}
              disabledOptions={orientationDisabled}
              dataTestId="orientation"
            />
          </Accordion.Item>
          <Accordion.Item
            value="pronouns"
            title={t('steps.identity.pronouns')}
            subtitle={t('steps.identity.pronounsTag')}
          >
            <PillToggle
              type="single"
              options={V90_PRONOUN_OPTIONS}
              value={pronouns}
              onValueChange={setPronouns}
              dataTestId="pronouns"
            />
          </Accordion.Item>
        </Accordion>
        <ContinueButton onClick={saveAndNext} disabled={!isValid}>
          {t('actions.continue')}
        </ContinueButton>
      </Flex>
    </WizardScreen>
  );
}
